import { forwardRef, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react'

import { cn } from './utils'

const fieldBase =
  'w-full rounded-xl border border-line bg-paper px-3 text-sm text-ink placeholder:text-ink-3 transition-colors ' +
  'hover:border-line-strong focus-visible:focus-ring disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-[invalid=true]:border-danger'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** `sm` is for dense forms (annotator properties panel). */
  fieldSize?: 'sm' | 'md'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ className, type = 'text', fieldSize = 'md', ...props }, ref) {
  return <input ref={ref} type={type} className={cn(fieldBase, fieldSize === 'sm' ? 'h-9' : 'h-11', className)} {...props} />
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ className, rows = 3, ...props }, ref) {
  return <textarea ref={ref} rows={rows} className={cn(fieldBase, 'min-h-11 resize-y py-2 leading-relaxed', className)} {...props} />
})

export interface FieldProps {
  label: string
  htmlFor?: string
  hint?: string
  error?: string
  className?: string
  children: React.ReactNode
}

/** Label + control + helper/error text, the three guidance layers of a form field. */
export function Field({ label, htmlFor, hint, error, className, children }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-ink-3">{hint}</p>
      ) : null}
    </div>
  )
}
